import { useState, useEffect } from 'react';
import { Eye, ChevronDown, Search, Trash, Edit3 } from 'lucide-react';
import { useAuth } from '../../context/authContext';
import { deleteNews, getAllNews, updateStatus, updateNews } from '../../services/newsServices';
import { ActionLoader, PageLoader } from '../../components/loader';
import Swal from 'sweetalert2';
import ArrowBack from '../../components/common/ArrowBack';
import {Banner} from '../../components/common/Banner'
import  SectionTips  from '../../components/common/SectionTips';
import  StatusBadge  from '../../components/news/StatusBadge';
import NewsRequestModal from '../../components/news/NewsRequestModal';
import NewsEditModal from '../../components/news/NewsEditModal';
import { Pagination } from '@mui/material';

// Componente principal
export default function NewsAdminPanel() {
    const [news, setNews] = useState([]);
    const [filteredNews, setFilteredNews] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('Todos los estados');
    const [selectedNews, setSelectedNews] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const { user } = useAuth(); 
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [page, setPage] = useState(1);
    const itemsPerPage = 10; 
    
    const fetchNewsRequests = async () => {
        try {
            setLoading(true);
            const response = await getAllNews(user);
            setNews(response);
        } catch (error){
            setError("Error fetching news requests");
            console.error("Error fetching news requests");
        } finally {
            setLoading(false);
        }
    };
    
    // Carga de noticias
    useEffect(() => {
        fetchNewsRequests();
    }, []);
    
    // Manejar búsqueda y filtros
    useEffect(() => {
        let result = [...news];
        
        // Filtrar por término de búsqueda
        if (searchTerm) {
        result = result.filter(item => 
            item.title.toLowerCase().includes(searchTerm.toLowerCase())
        );
        }
        
        // Filtrar por estado
        if (statusFilter !== 'Todos los estados') {
        result = result.filter(item => item.status === statusFilter);
        }
        
        setFilteredNews(result);
    }, [news, searchTerm, statusFilter]);
    
    const pageCount = Math.ceil(filteredNews.length / itemsPerPage);

    // Obtener las noticias de la página actual
    const paginatedNews = filteredNews.slice((page - 1) * itemsPerPage, page * itemsPerPage);
    
    // Función para eliminar una noticia
    const handleDelete = async (id) => {
        try {
            const confirmation = await Swal.fire({
                title: '¿Estás seguro de eliminar la noticia?',
                text: 'Esta acción no se puede deshacer',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#d33',
                cancelButtonColor: '#3085d6',
                confirmButtonText: 'Sí, eliminar',
                cancelButtonText: 'Cancelar',
            });
            if (!confirmation.isConfirmed) return;
            setIsSubmitting(true);
            const response = await deleteNews(id, user);
            setIsSubmitting(false);
            if (response.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Noticia eliminada',
                    showConfirmButton: false,
                    timer: 1500,
                });
            } else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error al eliminar la noticia',
                    timer: 1500,
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error al eliminar la noticia',
                timer: 1500,
            });
        }
        fetchNewsRequests(); 
        setIsSubmitting(false);
    }
    
    // Función para abrir el modal de detalles
    const openNewsModal = (news) => {
        setSelectedNews(news);
        setIsModalOpen(true);
    };

    // Función para abrir el modal de edición
    const handleEditNews = (newsItem) => {
        setSelectedNews(newsItem);
        setIsModalOpen(false); // Cerrar el modal de vista si está abierto
        setIsEditModalOpen(true);
    };

    // Función para guardar los cambios de edición
    const handleSaveEdit = async (newsId, editedData) => {
        const confirmation = await Swal.fire({
            title: '¿Estás seguro de guardar los cambios?',
            text: 'Esta acción no se puede deshacer',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sí, guardar cambios',
            cancelButtonText: 'Cancelar',
        });
        if (!confirmation.isConfirmed) return;
        try {
            setIsSubmitting(true);
            
            // Aquí necesitas crear/actualizar tu servicio updateNews
            const response = await updateNews(newsId, editedData, user);
            
            if (response.success) {
                // Actualizar el estado local
                setNews(prevNews => 
                    prevNews.map(n => 
                        n.id === newsId ? { ...n, ...editedData } : n
                    )
                );
                
                // Actualizar la noticia seleccionada
                setSelectedNews(prev => ({ ...prev, ...editedData }));
                
                setIsEditModalOpen(false);
                
                Swal.fire({
                    icon: 'success',
                    title: 'Noticia actualizada correctamente',
                    showConfirmButton: false,
                    timer: 1500,
                });
            } else {
                throw new Error('Error en la respuesta del servidor');
            }
        } catch (error) {
            console.error('Error saving news changes:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error al guardar los cambios',
                text: 'Por favor, intenta nuevamente',
                timer: 2000,
            });
            throw error; // Re-lanzar el error para que el modal pueda manejarlo
        } finally {
            setIsSubmitting(false);
        }
    };

    const estadoTraducido = {
        approved: "Aprobada",
        rejected: "Rechazada",
        pending: "Pendiente",
    };

    const handleChangeStatus = async (newsId, status) => {
        setIsModalOpen(false);
        try{
            const confirmation = await Swal.fire({
                title: '¿Estás seguro de cambiar el estado de la noticia?',
                text: 'Esta acción no se puede deshacer',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#3085d6',
                cancelButtonColor: '#d33',
                confirmButtonText: 'Sí, cambiar estado',
                cancelButtonText: 'Cancelar',
            });
            if (!confirmation.isConfirmed) return;
            setIsSubmitting(true);
            const response = await updateStatus(newsId, status, user);
            setIsSubmitting(false);
            if (response.success) {
                Swal.fire({
                    icon: 'success',
                    title: 'Estado actualizado',
                    showConfirmButton: false,
                    timer: 1500,
                });
            }
            else {
                Swal.fire({
                    icon: 'error',
                    title: 'Error al actualizar el estado',
                    timer: 1500,
                });
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error al actualizar el estado',
                timer: 1500,
            });
        }
        setIsSubmitting(false);
        fetchNewsRequests();
        
    }

    const parseDateToLocal = (dateString) => {
        if (!dateString) return null;
      
        const cleanDateString = dateString.split('T')[0]; // "2025-04-28"
        const parts = cleanDateString.split('-');
      
        if (parts.length !== 3) return null;
      
        const [year, month, day] = parts.map(Number);
      
        if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
      
        return new Date(year, month - 1, day);
      };      

    if (loading) return <PageLoader message='Cargando las solicitudes de noticias...'></PageLoader>
    if (error) return <div className="text-red-500 text-center">{error}</div>;
    
    return (
        <div className="min-h-screen bg-gray-100 py-8">
        <div className="container mx-auto px-4">
            <ArrowBack />
            <Banner title="Panel de Administración" message="Gestiona las solicitudes de noticias"></Banner>
            <SectionTips
                buttonTitle="¿Cómo usar esta sección?"
                section='el panel de administración'
                tipsTitle="Administración de solicitudes de noticias"
                tipsList={[
                    "Asegúrate de verificar la información antes de aprobar o rechazar.",
                    "Utiliza los filtros para encontrar rápidamente las solicitudes que necesitas.",
                    "Puedes editar las noticias antes de aprobarlas o rechazarlas.",
                    "Puedes eliminar solicitudes si es necesario.",
                ]}
                finalNote='Revisa las solicitudes de noticias regularmente.'
            />
            {/* Filtros y búsqueda */}
            <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
                
                {/* Filtro por estado */}
                <div className="w-full md:min-w-[16rem] md:w-64">
                <label className="block text-gray-700 font-medium mb-1">Estado</label>
                <div className="relative">
                    <select
                    className="block w-full appearance-none bg-white border border-gray-300 hover:border-gray-400 text-gray-700 py-3 px-4 pr-10 rounded-lg leading-tight focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    >
                    <option value="Todos los estados">Todos los estados</option>
                    <option value="approved">Aprobada</option>
                    <option value="pending">Pendiente</option>
                    <option value="rejected">Rechazada</option>
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-gray-500">
                    <ChevronDown size={16} />
                    </div>
                </div>
                </div>

                {/* Búsqueda por texto */}
                <div className="flex-1">
                <label className="block text-gray-700 font-medium mb-1">Buscar</label>
                <div className="relative">
                    <input
                    type="text"
                    placeholder="Buscar por título o resumen..."
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 hover:border-gray-400 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <div className="absolute left-3 top-3 text-gray-400">
                    <Search size={20} />
                    </div>
                </div>
                </div>

            </div>
            </div>

            {/* Indicador de filtros activos */}
            {(statusFilter !== "Todos los estados" || searchTerm.trim() !== "") && (
            <div className="mb-4 text-sm text-blue-700 bg-blue-50 border border-blue-200 px-4 py-2 rounded-lg">
                Mostrando resultados filtrados
                {statusFilter !== "Todos los estados" && (
                <> por estado: "<strong>{estadoTraducido[statusFilter] || statusFilter}</strong>"</>
                )}
                {searchTerm.trim() && (
                <> y búsqueda: "<strong>{searchTerm}</strong>"</>
                )}
            </div>
            )}

            {/* Vista de tabla para pantallas medianas hacia arriba */}
            <div className="hidden md:block bg-white rounded-lg shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full bg-white text-sm">
                <thead>
                    <tr className="bg-gray-50 border-b">
                    <th className="px-6 py-4 text-left text-gray-700 font-semibold">Título</th>
                    <th className="px-6 py-4 text-left text-gray-700 font-semibold">Categoría</th>
                    <th className="px-6 py-4 text-left text-gray-700 font-semibold">Fecha</th>
                    <th className="px-6 py-4 text-left text-gray-700 font-semibold">Estado</th>
                    <th className="px-6 py-4 text-right text-gray-700 font-semibold">Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    {paginatedNews.map((item, index) => (
                    <tr
                        key={item.id}
                        className={`border-t hover:bg-blue-50 transition-colors duration-150 ${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}
                    >
                        <td className="px-6 py-4 max-w-xs truncate cursor-pointer hover:text-blue-600" onClick={() => openNewsModal(item)} title={item.title}>
                        {item.title}
                        </td>
                        <td className="px-6 py-4">{item.category}</td>
                        <td className="px-6 py-4">{item.creationDate && parseDateToLocal(item.creationDate).toLocaleDateString()}</td>
                        <td className="px-6 py-4"><StatusBadge status={item.status} /></td>
                        <td className="px-6 py-4 text-right">
                        <div className="flex justify-end gap-2">
                            <button onClick={() => handleEditNews(item)} className="flex items-center gap-1 text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 px-3 py-1 rounded-md transition-colors">
                            <Edit3 size={18} />
                            <span className="hidden md:inline">Editar</span>
                            </button>
                            <button onClick={() => openNewsModal(item)} className="flex items-center gap-1 text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 px-3 py-1 rounded-md transition-colors">
                            <Eye size={18} />
                            <span className="hidden md:inline">Ver</span>
                            </button>
                            <button onClick={() => handleDelete(item.id)} className="flex items-center gap-1 text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 px-3 py-1 rounded-md transition-colors">
                            <Trash size={18} />
                            <span className="hidden md:inline">Eliminar</span>
                            </button>
                        </div>
                        </td>
                    </tr>
                    ))}
                    {filteredNews.length === 0 && (
                    <tr>
                        <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                        No se encontraron noticias
                        </td>
                    </tr>
                    )}
                </tbody>
                </table>
            </div>
            </div>

            {/* Vista móvil: tarjetas */}
            <div className="md:hidden space-y-4">
            {filteredNews.length > 0 ? (
                filteredNews.map((item) => (
                <div key={item.id} className="bg-white rounded-lg shadow-sm p-4">
                    <h3 className="text-lg font-semibold truncate" title={item.title}>{item.title}</h3>
                    <p className="text-sm text-gray-600">{item.category}</p>
                    <p className="text-sm text-gray-600">{new Date(item.creationDate).toLocaleDateString()}</p>
                    <div className="mt-2">
                    <StatusBadge status={item.status} />
                    </div>
                    <div className="flex justify-end gap-2 mt-4">
                    <button onClick={() => handleEditNews(item)} className="text-blue-600 flex items-center gap-1">
                        <Edit3 size={18} />
                    </button>
                    <button onClick={() => openNewsModal(item)} className="text-green-600 flex items-center gap-1">
                        <Eye size={18} />
                    </button>
                    <button onClick={() => handleDelete(item.id)} className="text-red-600 flex items-center gap-1">
                        <Trash size={18} />
                    </button>
                    </div>
                </div>
                ))
            ) : (
                <div className="text-center text-gray-500 mt-8">No se encontraron noticias</div>
            )}
            </div>

            {/* Modal de vista/aprobación */}
            {selectedNews && (
                <NewsRequestModal
                news={selectedNews}
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                handleChangeStatus={handleChangeStatus}
                onEdit={handleEditNews}
                />
            )}

            {/* Modal de edición */}
            {selectedNews && (
                <NewsEditModal
                news={selectedNews}
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                onSave={handleSaveEdit}
                />
            )}
        
        {/* Estilos globales para animaciones */}
        <style>{`
            @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
            }
            
            @keyframes modalEntry {
            from { 
                opacity: 0;
                transform: scale(0.95);
            }
            to { 
                opacity: 1;
                transform: scale(1);
            }
            }
            
            .animate-fadeIn {
            animation: fadeIn 0.3s ease-out;
            }
            
            .animate-modalEntry {
            animation: modalEntry 0.3s ease-out;
            }
        `}</style>
        </div>
        {/* Paginación */}
        {pageCount > 1 && (
            <div className="flex justify-center mt-4 md:mt-5">
                <Pagination
                    count={pageCount}
                    page={page}
                    onChange={(event, value) => setPage(value)}
                    color="primary"
                    size="small"
                    siblingCount={0}
                    className="scale-90 md:scale-100"
                />
            </div>
        )}
        <ActionLoader open={isSubmitting} onClose={() => {}} />
        </div>
    );
}